import React, { useEffect, useState } from 'react';
import { Plus, Loader2, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { payroll } from '../../api';
import { currency } from '../billing/shared';
import { PayRunEditor } from './PayRunEditor';

const iso = (d) => d.toISOString().slice(0, 10);
const defaults = (freq) => {
  const now = new Date();
  if (freq === 'monthly') {
    const s = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
    const e = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0));
    return { periodStart: iso(s), periodEnd: iso(e), payDate: iso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), Math.min(25, e.getUTCDate())))) };
  }
  const days = freq === 'weekly' ? 7 : 14;
  const s = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7)));
  const e = new Date(s.getTime() + (days - 1) * 86400000);
  return { periodStart: iso(s), periodEnd: iso(e), payDate: iso(new Date(e.getTime() - 86400000 * 2)) };
};

const NewRunDialog = ({ open, onOpenChange, clientId, onCreated, toast }) => {
  const [f, setF] = useState({ frequency: 'monthly', ...defaults('monthly') });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ frequency: 'monthly', ...defaults('monthly') }); }, [open]);
  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    payroll.createRun(clientId, f).then((r) => { toast({ title: 'Pay run created', description: `${r.payslips.length} payslips calculated` }); onOpenChange(false); onCreated(r); })
      .catch((err) => toast({ title: 'Could not create pay run', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }))
      .finally(() => setBusy(false));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>New pay run</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Frequency</Label>
            <Select value={f.frequency} onValueChange={(v) => setF({ frequency: v, ...defaults(v) })}>
              <SelectTrigger className="mt-1.5" data-testid="new-run-frequency-select"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="fortnightly">Fortnightly</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="nr-s">Period start</Label><Input id="nr-s" type="date" required value={f.periodStart} onChange={(e) => setF({ ...f, periodStart: e.target.value })} className="mt-1.5" data-testid="new-run-start-input"/></div>
            <div><Label htmlFor="nr-e">Period end</Label><Input id="nr-e" type="date" required value={f.periodEnd} onChange={(e) => setF({ ...f, periodEnd: e.target.value })} className="mt-1.5" data-testid="new-run-end-input"/></div>
          </div>
          <div><Label htmlFor="nr-p">Pay date (decides EMP201 month)</Label><Input id="nr-p" type="date" required value={f.payDate} onChange={(e) => setF({ ...f, payDate: e.target.value })} className="mt-1.5" data-testid="new-run-paydate-input"/></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="new-run-submit-btn">{busy && <Loader2 size={14} className="mr-2 animate-spin"/>} Calculate payslips</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PayRunsTab = ({ clientId, toast, onChanged }) => {
  const [runs, setRuns] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = () => payroll.runs(clientId).then(setRuns);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientId]);
  const changed = () => { load(); onChanged?.(); };

  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="payroll-runs-tab">
      <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold text-stone-900"><CalendarDays size={16} className="text-emerald-800"/> Pay runs</div>
        <Button size="sm" onClick={() => setNewOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="new-payrun-btn"><Plus size={14} className="mr-1.5"/> New pay run</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="payruns-table">
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
            <tr>{['Period', 'Frequency', 'Pay date', 'Employees', 'Gross', 'PAYE', 'Net', 'Status'].map((h, i) => <th key={h} className={`px-5 py-3 font-medium ${i >= 3 && i <= 6 ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {runs && runs.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-stone-500" data-testid="payruns-empty">No pay runs yet.</td></tr>}
            {runs?.map((r) => (
              <tr key={r.id} onClick={() => setOpenId(r.id)} className="hover:bg-stone-50 cursor-pointer" data-testid={`payrun-row-${r.id}`}>
                <td className="px-5 py-3 font-medium text-stone-900">{r.periodLabel}</td>
                <td className="px-5 py-3 capitalize text-stone-600">{r.frequency}</td>
                <td className="px-5 py-3 text-stone-600">{r.payDate}</td>
                <td className="px-5 py-3 text-right">{r.totals.employees}</td>
                <td className="px-5 py-3 text-right tabular-nums">{currency(r.totals.gross)}</td>
                <td className="px-5 py-3 text-right tabular-nums">{currency(r.totals.paye)}</td>
                <td className="px-5 py-3 text-right tabular-nums font-semibold">{currency(r.totals.net)}</td>
                <td className="px-5 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${r.status === 'draft' ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>{r.status === 'draft' ? 'Draft' : 'Finalised'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NewRunDialog open={newOpen} onOpenChange={setNewOpen} clientId={clientId} toast={toast} onCreated={(r) => { changed(); setOpenId(r.id); }}/>
      <PayRunEditor runId={openId} onOpenChange={(o) => { if (!o) setOpenId(null); }} onChanged={changed} toast={toast}/>
    </div>
  );
};
